import {computed, DOCUMENT, inject, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {filterNulls} from '@brunata-angular/util';
import {
    distinctUntilChanged,
    EMPTY,
    fromEvent,
    map,
    merge,
    startWith,
    Subject,
    switchMap,
} from 'rxjs';
import {samePosition, settledPosition, StepPosition} from './step-state.model';

export type StepAnchor = {
    stepId: Signal<string>;
    element: HTMLElement;
};

type StepTrack = {
    scroller: Element;
    stepIds: string[];
    tops: number[];
    starts: number[];
    wheelStarts: number[];
};

type TrackPosition = {
    index: number;
    scrollTop: number;
    travel: number;
};

const baseWheelPerStep = 100;
const wheelPerPixel = 0.6;
const stepStopMilliseconds = 300;
const stepTopMargin = 16;
const pixelsPerWheelLine = 16;
const handoverShare = 0.2;

@Injectable()
export class StepFocusService {
    private readonly document = inject(DOCUMENT);
    private readonly anchors = new Set<StepAnchor>();
    private readonly picked$ = new Subject<string>();
    private readonly entered$ = new Subject<string>();
    private readonly resized$ = new Subject<void>();
    private readonly resizeObserver = new ResizeObserver(() => this.resized$.next());

    private trackPosition = 0;
    private steeredScrollTop: number | null = null;
    private chosenStepId: string | null = null;
    private stepScroller: Element | null = null;
    private stoppedUntil = 0;

    private readonly userScrolls$ = merge(
        fromEvent(this.document, 'wheel', {passive: true}),
        fromEvent(this.document, 'touchmove', {passive: true}),
        fromEvent(this.document, 'keydown'),
        fromEvent(this.document, 'pointerdown', {capture: true, passive: true}),
        fromEvent(this.document, 'scrollend', {capture: true, passive: true}),
    );

    private readonly followsScroll$ = merge(
        this.picked$.pipe(map(() => false)),
        this.userScrolls$.pipe(map(() => true)),
    ).pipe(startWith(true), distinctUntilChanged());

    private readonly steered$ = fromEvent<WheelEvent>(this.document, 'wheel', {
        passive: false,
    }).pipe(
        map(event => this.steer(event)),
        filterNulls(),
    );

    private readonly scrolled$ = fromEvent(this.document, 'scroll', {
        capture: true,
        passive: true,
    }).pipe(
        map(event => this.followNativeScroll(event.target)),
        filterNulls(),
    );

    readonly position = toSignal(
        merge(
            this.picked$.pipe(map(stepId => this.landingOf(stepId))),
            this.entered$.pipe(map(settledPosition)),
            this.steered$,
            this.resized$.pipe(
                map(() => this.followLayout()),
                filterNulls(),
            ),
            this.followsScroll$.pipe(switchMap(follows => (follows ? this.scrolled$ : EMPTY))),
        ).pipe(distinctUntilChanged(samePosition)),
        {initialValue: null},
    );

    readonly currentId = computed(() => this.position()?.stepId ?? null);

    register(anchor: StepAnchor): () => void {
        this.anchors.add(anchor);
        this.resizeObserver.observe(anchor.element);
        return () => {
            this.anchors.delete(anchor);
            this.resizeObserver.unobserve(anchor.element);
        };
    }

    select(stepId: string): void {
        const anchor = [...this.anchors].find(candidate => candidate.stepId() === stepId);
        anchor?.element.scrollIntoView({behavior: 'smooth', block: 'start'});
        this.chosenStepId = stepId;
        this.picked$.next(stepId);
    }

    enter(stepId: string): void {
        if (this.currentId() === stepId) {
            return;
        }
        this.chosenStepId = stepId;
        this.entered$.next(stepId);
    }

    private steer(event: WheelEvent): StepPosition | null {
        if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            return null;
        }
        const track = this.trackFor(this.stepScrollerFor(event.target, event.deltaY > 0));
        if (track === null) {
            return null;
        }
        event.preventDefault();
        this.stepScroller = track.scroller;
        if (event.timeStamp < this.stoppedUntil) {
            return null;
        }
        const chosenIndex =
            this.chosenStepId === null ? -1 : track.stepIds.indexOf(this.chosenStepId);
        this.chosenStepId = null;
        if (chosenIndex !== -1) {
            this.trackPosition = trackPositionIn(track, chosenIndex, track.tops[chosenIndex]);
        } else if (
            this.steeredScrollTop === null ||
            Math.abs(track.scroller.scrollTop - this.steeredScrollTop) > 1
        ) {
            this.trackPosition = trackPositionFor(track, track.scroller.scrollTop);
        }
        const delta =
            event.deltaMode === WheelEvent.DOM_DELTA_LINE
                ? event.deltaY * pixelsPerWheelLine
                : event.deltaY;
        const lastIndex = track.stepIds.length - 1;
        const end =
            track.starts[lastIndex + 1] > track.starts[lastIndex]
                ? track.wheelStarts[lastIndex + 1] - 1
                : track.wheelStarts[lastIndex];
        const lowerEdge = Math.max(
            0,
            ...track.wheelStarts.filter(edge => edge < this.trackPosition),
        );
        const upperEdge =
            track.wheelStarts.find(edge => edge > this.trackPosition) ??
            track.wheelStarts[lastIndex + 1];
        this.trackPosition = Math.min(
            Math.max(this.trackPosition + delta, lowerEdge, 0),
            upperEdge,
            end,
        );
        if (this.trackPosition === lowerEdge || this.trackPosition === upperEdge) {
            this.stoppedUntil = event.timeStamp + stepStopMilliseconds;
        }
        const {index, scrollTop, travel} = scrollPositionFor(track, this.trackPosition);
        this.steeredScrollTop = scrollTop;
        track.scroller.scrollTop = scrollTop;
        return {stepId: track.stepIds[index], travel};
    }

    private landingOf(stepId: string): StepPosition {
        const track = this.trackFor(this.stepScroller ?? this.stepScrollerOfAnchors());
        const index = track === null ? -1 : track.stepIds.indexOf(stepId);
        return track === null || index === -1
            ? settledPosition(stepId)
            : {stepId, travel: shareOf(track, index, track.tops[index])};
    }

    private followNativeScroll(target: EventTarget | null): StepPosition | null {
        const scroller = target instanceof Document ? target.scrollingElement : target;
        if (!(scroller instanceof Element)) {
            return null;
        }
        const track = this.trackFor(scroller);
        if (track === null) {
            return null;
        }
        if (
            this.steeredScrollTop !== null &&
            Math.abs(scroller.scrollTop - this.steeredScrollTop) <= 1
        ) {
            return null;
        }
        this.steeredScrollTop = null;
        this.chosenStepId = null;
        this.stepScroller = scroller;
        const kept = this.keepCurrentStep(track);
        if (kept !== null) {
            return kept;
        }
        this.trackPosition = trackPositionFor(track, scroller.scrollTop);
        const {index, travel} = scrollPositionFor(track, this.trackPosition);
        return {stepId: track.stepIds[index], travel};
    }

    private followLayout(): StepPosition | null {
        const track = this.trackFor(this.stepScroller ?? this.stepScrollerOfAnchors());
        const settled = this.position();
        if (track === null || settled === null) {
            return null;
        }
        const kept = this.keepCurrentStep(track);
        const index = track.stepIds.indexOf(settled.stepId);
        if (kept === null && index !== -1 && track.scroller.scrollTop < track.starts[index]) {
            this.select(settled.stepId);
        }
        return kept;
    }

    private keepCurrentStep(track: StepTrack): StepPosition | null {
        const settled = this.position();
        const index = settled === null ? -1 : track.stepIds.indexOf(settled.stepId);
        if (settled === null || index === -1) {
            return null;
        }
        const from = track.starts[index];
        const to = track.starts[index + 1];
        const {scrollTop} = track.scroller;
        if (scrollTop < from - 1 || scrollTop > to + 1) {
            return null;
        }
        const travel = to > from ? shareOf(track, index, scrollTop) : settled.travel;
        this.steeredScrollTop = scrollTop;
        this.trackPosition = wheelPositionIn(track, index, travel);
        return {stepId: settled.stepId, travel};
    }

    private stepScrollerOfAnchors(): Element | null {
        const anchor = [...this.anchors].at(0);
        return anchor === undefined ? null : this.stepScrollerFor(anchor.element, true);
    }

    private stepScrollerFor(target: EventTarget | null, downwards: boolean): Element | null {
        for (
            let element = target instanceof Element ? target : null;
            element !== null;
            element = element.parentElement
        ) {
            if (!scrolls(element)) {
                continue;
            }
            if (this.holdsSteps(element)) {
                return element;
            }
            const canMove = downwards
                ? element.scrollTop + element.clientHeight < element.scrollHeight - 1
                : element.scrollTop > 0;
            if (canMove) {
                return null;
            }
        }
        const root = this.document.scrollingElement;
        return root !== null && this.pageScrolls(root) ? root : null;
    }

    private pageScrolls(root: Element): boolean {
        return (
            root.scrollHeight > root.clientHeight &&
            [root, this.document.body].every(
                element => getComputedStyle(element).overflowY !== 'hidden',
            )
        );
    }

    private trackFor(scroller: Element | null): StepTrack | null {
        if (scroller === null || !this.holdsSteps(scroller)) {
            return null;
        }
        const isRoot = scroller === this.document.scrollingElement;
        const viewportTop = isRoot ? 0 : scroller.getBoundingClientRect().top;
        const viewportHeight = isRoot
            ? (this.document.defaultView?.innerHeight ?? scroller.clientHeight)
            : scroller.clientHeight;
        const maxScroll = scroller.scrollHeight - viewportHeight;
        if (maxScroll <= 0) {
            return null;
        }
        const cards = [...this.anchors]
            .map(anchor => ({
                stepId: anchor.stepId(),
                top: anchor.element.getBoundingClientRect().top - viewportTop + scroller.scrollTop,
                topMargin: scrollMarginTop(anchor.element),
            }))
            .sort((first, second) => first.top - second.top);
        const tops = [
            0,
            ...cards
                .slice(1)
                .map(card => Math.min(Math.max(card.top - card.topMargin, 0), maxScroll)),
            maxScroll,
        ];
        const starts = tops.map((top, index) =>
            index === 0 || top >= maxScroll
                ? top
                : top - handoverShare * Math.min(viewportHeight, top - tops[index - 1]),
        );
        return {
            scroller,
            stepIds: cards.map(card => card.stepId),
            tops,
            starts,
            wheelStarts: wheelStartsFor(starts),
        };
    }

    private holdsSteps(element: Element): boolean {
        return [...this.anchors].some(anchor => element.contains(anchor.element));
    }
}

function scrolls(element: Element): boolean {
    const {overflowY} = getComputedStyle(element);
    return (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        element.scrollHeight > element.clientHeight
    );
}

function scrollMarginTop(element: Element): number {
    const margin = Number.parseFloat(getComputedStyle(element).scrollMarginTop);
    return Number.isNaN(margin) ? stepTopMargin : margin;
}

function wheelStartsFor(starts: ReadonlyArray<number>): number[] {
    return starts.reduce<number[]>(
        (wheelStarts, start, index) => [
            ...wheelStarts,
            index === 0
                ? 0
                : wheelStarts[index - 1] +
                  baseWheelPerStep +
                  wheelPerPixel * (start - starts[index - 1]),
        ],
        [],
    );
}

function scrollPositionFor(track: StepTrack, position: number): TrackPosition {
    const index = Math.max(
        Math.min(
            track.wheelStarts.filter(edge => edge <= position).length - 1,
            track.stepIds.length - 1,
        ),
        0,
    );
    const wheelFrom = track.wheelStarts[index];
    const travel = Math.min((position - wheelFrom) / (track.wheelStarts[index + 1] - wheelFrom), 1);
    const from = track.starts[index];
    const to = track.starts[index + 1];
    return {
        index,
        scrollTop: from + (to - from) * travel,
        travel,
    };
}

function trackPositionFor(track: StepTrack, scrollTop: number): number {
    const last = track.stepIds.length - 1;
    const index = track.stepIds.findIndex(
        (_, candidate) => candidate === last || scrollTop < track.starts[candidate + 1],
    );
    return trackPositionIn(track, index, scrollTop);
}

function trackPositionIn(track: StepTrack, index: number, scrollTop: number): number {
    return wheelPositionIn(track, index, shareOf(track, index, scrollTop));
}

function wheelPositionIn(track: StepTrack, index: number, travel: number): number {
    const wheelFrom = track.wheelStarts[index];
    return wheelFrom + (track.wheelStarts[index + 1] - wheelFrom) * travel;
}

function shareOf(track: StepTrack, index: number, scrollTop: number): number {
    const from = track.starts[index];
    const to = track.starts[index + 1];
    return to > from ? Math.min(Math.max((scrollTop - from) / (to - from), 0), 1) : 0;
}
